import React, { useEffect, useMemo, useState } from 'react';
import {
  ArrowLeftIcon,
  ArrowRightIcon,
  ArrowsRightLeftIcon,
  CheckCircleIcon,
  ClockIcon,
  DocumentTextIcon,
  LightBulbIcon,
  ListBulletIcon,
  PaperAirplaneIcon,
  ChatBubbleLeftRightIcon,
  SparklesIcon,
  WrenchScrewdriverIcon,
  XCircleIcon
} from '@heroicons/react/24/outline';
import apiClient from '../../utils/apiClient';
import {
  AnlagenForm,
  ART_LABEL,
  Einordnung,
  KatalogEintrag,
  Kandidat,
  KiErklaerung,
  SPHAEREN,
  Status
} from './types';
import { confidenceLabel, euro } from './utils';

interface Props {
  status: Status | null;
  kandidat?: Kandidat;
  form: AnlagenForm;
  setForm: React.Dispatch<React.SetStateAction<AnlagenForm>>;
  vorschau: Einordnung | null;
  katalog: KatalogEintrag[];
  laedt: boolean;
  speichert: boolean;
  onDetails: () => void;
  onEntscheiden: (entscheidung: 'anlage' | 'kein_anlagegut' | 'umbuchung' | 'zurueckgestellt', notiz?: string) => void;
  onZumBestand: () => void;
}

const stepClass = (active: boolean, done: boolean) =>
  `flex min-w-0 flex-1 items-center gap-3 rounded-xl border px-3 py-3 text-left transition-colors ${
    active
      ? 'border-primary-300 bg-primary-50 text-primary-800 dark:border-primary-700 dark:bg-primary-900/30 dark:text-primary-200'
      : done
        ? 'border-green-200 bg-green-50 text-green-800 dark:border-green-800 dark:bg-green-950/20 dark:text-green-200'
        : 'border-gray-200 bg-white text-gray-500 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-400'
  }`;

const EmpfehlungBadge: React.FC<{ empfehlung: string }> = ({ empfehlung }) => {
  const map: Record<string, { text: string; cls: string }> = {
    anlage: { text: 'Mögliche Anschaffung', cls: 'bg-primary-50 text-primary-700 border-primary-200 dark:bg-primary-900/30 dark:text-primary-300 dark:border-primary-800' },
    kein_anlagegut: { text: 'Wahrscheinlich laufender Aufwand', cls: 'bg-amber-50 text-amber-800 border-amber-200 dark:bg-amber-950/30 dark:text-amber-200 dark:border-amber-800' },
    umbuchung: { text: 'Wahrscheinlich Umbuchung', cls: 'bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-950/30 dark:text-blue-300 dark:border-blue-800' },
    unklar: { text: 'Noch unklar', cls: 'bg-gray-100 text-gray-700 border-gray-200 dark:bg-gray-700 dark:text-gray-200 dark:border-gray-600' }
  };
  const wert = map[empfehlung] || map.unklar;
  return <span className={`inline-flex rounded-full border px-2.5 py-1 text-xs font-bold ${wert.cls}`}>{wert.text}</span>;
};

const AnlagenAssistant: React.FC<Props> = ({
  status, kandidat, form, setForm, vorschau, katalog, laedt, speichert,
  onDetails, onEntscheiden, onZumBestand
}) => {
  const [schritt, setSchritt] = useState(1);
  const [typ, setTyp] = useState<'gegenstand' | 'leistung' | 'umbuchung' | null>(null);
  const [ki, setKi] = useState<KiErklaerung | null>(null);
  const [kiLaedt, setKiLaedt] = useState(false);
  const [kiFehler, setKiFehler] = useState('');
  const [kiFrage, setKiFrage] = useState('');
  const [kiAntwortLaedt, setKiAntwortLaedt] = useState(false);
  const [kiDialogFehler, setKiDialogFehler] = useState('');
  const [kiDialog, setKiDialog] = useState<Array<{
    rolle: 'user' | 'assistant';
    text: string;
    quelle?: 'ki' | 'regelwerk';
  }>>([]);

  useEffect(() => {
    setSchritt(1);
    setTyp(null);
    setKi(null);
    setKiFehler('');
    setKiFrage('');
    setKiAntwortLaedt(false);
    setKiDialogFehler('');
    setKiDialog([]);
  }, [kandidat?.id]);

  const geprueft = status ? status.gesamt - status.offen : 0;
  const fortschritt = status?.gesamt ? Math.round((geprueft / status.gesamt) * 100) : 0;
  const eo = vorschau || kandidat?.einordnung;
  const aktiveKategorie = katalog.find((item) => item.key === form.kategorie);

  const kiFragen = useMemo(() => ki?.prueffragen || [], [ki]);

  const kiLaden = async () => {
    if (!kandidat) return;
    setKiLaedt(true);
    setKiFehler('');
    try {
      const response = await apiClient.post('/api/anlagen/assistent/ki-erklaerung', { buchung_id: kandidat.id });
      setKi(response.data.data);
    } catch (error: any) {
      setKiFehler(error.response?.data?.message || 'Die Erklärhilfe ist gerade nicht erreichbar. Bitte prüfe den Beleg manuell.');
    } finally {
      setKiLaedt(false);
    }
  };

  const kiUebernehmen = () => {
    if (!ki) return;
    const kategorie = katalog.find((item) => item.key === ki.vorgeschlagene_kategorie);
    setForm((alt) => ({
      ...alt,
      bezeichnung: ki.vorgeschlagene_bezeichnung || alt.bezeichnung,
      kategorie: kategorie?.key || alt.kategorie,
      nutzungsdauer: ki.vorgeschlagene_nutzungsdauer || kategorie?.jahre || alt.nutzungsdauer
    }));
    if (ki.empfehlung === 'anlage') {
      setTyp('gegenstand');
      setSchritt(2);
    }
  };

  const kiFrageSenden = async (event: React.FormEvent) => {
    event.preventDefault();
    const frage = kiFrage.trim();
    if (!kandidat || !frage || kiAntwortLaedt) return;

    const verlauf = kiDialog.map(({ rolle, text }) => ({ rolle, text }));
    setKiDialog((alt) => [...alt, { rolle: 'user', text: frage }]);
    setKiFrage('');
    setKiAntwortLaedt(true);
    setKiDialogFehler('');

    try {
      const response = await apiClient.post('/api/anlagen/assistent/ki-frage', {
        buchung_id: kandidat.id,
        frage,
        verlauf,
        benutzer_kontext: {
          ausgewaehlter_typ: typ,
          menge: form.menge,
          selbststaendig_nutzbar: form.selbststaendig_nutzbar,
          ust_satz: form.ust_satz,
          vorgeschlagenes_ergebnis: ART_LABEL[eo?.art || ''] || eo?.art,
          begruendung: eo?.begruendung,
          bezeichnung: form.bezeichnung,
          kategorie: form.kategorie,
          nutzungsdauer: form.nutzungsdauer,
          sphaere: form.sphaere,
          inbetriebnahme: form.inbetriebnahme
        }
      });
      setKiDialog((alt) => [...alt, {
        rolle: 'assistant',
        text: response.data.data.antwort,
        quelle: response.data.data.quelle
      }]);
    } catch (error: any) {
      setKiDialogFehler(error.response?.data?.message || 'Die KI konnte die Frage gerade nicht beantworten.');
    } finally {
      setKiAntwortLaedt(false);
    }
  };

  if (laedt) {
    return (
      <div className="card border border-gray-200 p-10 text-center dark:border-gray-700">
        <span className="spinner mx-auto" aria-hidden="true" />
        <p className="mt-4 text-sm font-semibold text-gray-700 dark:text-gray-200">Buchungen werden vorbereitet …</p>
      </div>
    );
  }

  if (!kandidat || !eo) {
    return (
      <div className="card border border-gray-200 p-8 text-center dark:border-gray-700 sm:p-12">
        <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-green-100 text-green-700 dark:bg-green-950/30 dark:text-green-300">
          <CheckCircleIcon className="h-8 w-8" />
        </div>
        <h2 className="mt-5 text-2xl font-bold text-gray-900 dark:text-white">Der Durchlauf ist geschafft</h2>
        <p className="mx-auto mt-2 max-w-2xl text-sm leading-6 text-gray-600 dark:text-gray-300">
          Alle auffälligen Bankbuchungen wurden angesehen. Im Anlagenverzeichnis kannst du das Ergebnis kontrollieren und älteren Bestand ergänzen.
        </p>
        <button className="btn primary mt-6" onClick={onZumBestand}>Anlagenverzeichnis öffnen <ArrowRightIcon className="h-4 w-4" /></button>
      </div>
    );
  }

  const typWaehlen = (wert: 'gegenstand' | 'leistung' | 'umbuchung') => {
    setTyp(wert);
    if (wert === 'gegenstand') setSchritt(2);
    else setSchritt(3);
  };

  return (
    <div className="space-y-5">
      <section className="card border border-gray-200 p-5 dark:border-gray-700" aria-labelledby="durchlauf-title">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.18em] text-primary-600 dark:text-primary-400">Dein Prüfdurchlauf</p>
            <h2 id="durchlauf-title" className="mt-1 text-lg font-bold text-gray-900 dark:text-white">{status?.offen || 0} Buchungen brauchen noch eine Antwort</h2>
          </div>
          <div className="text-right">
            <p className="text-2xl font-bold text-gray-900 dark:text-white">{fortschritt} %</p>
            <p className="text-xs text-gray-500 dark:text-gray-400">{geprueft} von {status?.gesamt || 0} erledigt</p>
          </div>
        </div>
        <div className="mt-4 h-2 overflow-hidden rounded-full bg-gray-200 dark:bg-gray-700" role="progressbar" aria-label="Prüffortschritt" aria-valuenow={fortschritt} aria-valuemin={0} aria-valuemax={100}>
          <div className="h-full rounded-full bg-primary-600 transition-all" style={{ width: `${fortschritt}%` }} />
        </div>
      </section>

      <nav className="grid gap-2 sm:grid-cols-3" aria-label="Schritte des Anlagen-Assistenten">
        {[
          { nr: 1, title: 'Was wurde bezahlt?', sub: 'Beleg und Art der Ausgabe' },
          { nr: 2, title: 'Wie wird es genutzt?', sub: 'Stückzahl und Nutzbarkeit' },
          { nr: 3, title: 'Entscheidung prüfen', sub: 'Ergebnis bestätigen' }
        ].map((item) => (
          <button key={item.nr} className={stepClass(schritt === item.nr, schritt > item.nr)} onClick={() => item.nr < schritt && setSchritt(item.nr)} disabled={item.nr > schritt}>
            <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-current text-sm font-bold">
              {schritt > item.nr ? <CheckCircleIcon className="h-5 w-5" /> : item.nr}
            </span>
            <span className="min-w-0">
              <span className="block truncate text-sm font-bold">{item.title}</span>
              <span className="block truncate text-xs opacity-75">{item.sub}</span>
            </span>
          </button>
        ))}
      </nav>

      <section className="card overflow-hidden border border-gray-200 dark:border-gray-700">
        <div className="border-b border-gray-200 bg-gray-50 p-5 dark:border-gray-700 dark:bg-gray-800/70 sm:p-6">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2 text-xs font-semibold text-gray-500 dark:text-gray-400">
                <span>{kandidat.datum}</span><span aria-hidden="true">•</span><span>{kandidat.konto || 'Konto unbekannt'}</span><span aria-hidden="true">•</span><span>{kandidat.kategorien || 'Ohne Kategorie'}</span>
              </div>
              <h3 className="mt-2 break-words text-lg font-bold text-gray-900 dark:text-white">
                {kandidat.name_zahlungsbeteiligter || kandidat.verwendungszweck || 'Buchung ohne Beschreibung'}
              </h3>
              <p className="mt-1 line-clamp-2 text-sm text-gray-600 dark:text-gray-300">{kandidat.verwendungszweck || 'Kein Verwendungszweck vorhanden'}</p>
            </div>
            <div className="shrink-0 sm:text-right">
              <p className="text-2xl font-bold text-gray-900 dark:text-white">{euro(kandidat.brutto)}</p>
              <button className="mt-2 inline-flex min-h-10 items-center gap-2 rounded-xl text-sm font-bold text-primary-700 hover:text-primary-800 dark:text-primary-300" onClick={onDetails}>
                <DocumentTextIcon className="h-4 w-4" /> {kandidat.belege ? `${kandidat.belege} Beleg${kandidat.belege === 1 ? '' : 'e'} ansehen` : 'Buchungsdetails ansehen'}
              </button>
            </div>
          </div>
        </div>

        {schritt === 1 && (
          <div className="space-y-6 p-5 sm:p-6">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.16em] text-primary-600 dark:text-primary-400">Frage 1 von 3</p>
              <h2 className="mt-1 text-xl font-bold text-gray-900 dark:text-white">Was hat der Verein für dieses Geld bekommen?</h2>
              <p className="mt-2 max-w-3xl text-sm leading-6 text-gray-600 dark:text-gray-300">Öffne im Zweifel den Beleg. Entscheidend ist nicht der Betrag, sondern ob ein neuer Gegenstand entstanden ist, der länger genutzt wird.</p>
            </div>

            <div className="grid gap-3 lg:grid-cols-3">
              <button className="group rounded-2xl border border-gray-200 bg-white p-4 text-left hover:border-primary-400 hover:bg-primary-50/50 dark:border-gray-700 dark:bg-gray-800 dark:hover:border-primary-600 dark:hover:bg-primary-900/20" onClick={() => typWaehlen('gegenstand')}>
                <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary-100 text-primary-700 dark:bg-primary-900/40 dark:text-primary-300"><WrenchScrewdriverIcon className="h-5 w-5" /></span>
                <span className="mt-3 block font-bold text-gray-900 dark:text-white">Ein Gegenstand oder eine neue Anlage</span>
                <span className="mt-1 block text-sm leading-5 text-gray-600 dark:text-gray-300">Zum Beispiel Möbel, Computer, Zaun, Gerät oder eine neu gebaute Einrichtung.</span>
              </button>
              <button className="group rounded-2xl border border-gray-200 bg-white p-4 text-left hover:border-amber-400 hover:bg-amber-50/50 dark:border-gray-700 dark:bg-gray-800 dark:hover:border-amber-600 dark:hover:bg-amber-950/20" onClick={() => typWaehlen('leistung')}>
                <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-amber-100 text-amber-700 dark:bg-amber-950/40 dark:text-amber-300"><ListBulletIcon className="h-5 w-5" /></span>
                <span className="mt-3 block font-bold text-gray-900 dark:text-white">Eine laufende Leistung oder Verbrauch</span>
                <span className="mt-1 block text-sm leading-5 text-gray-600 dark:text-gray-300">Zum Beispiel Pflege, Reparatur, Beitrag, Miete, Versicherung oder Entsorgung.</span>
              </button>
              <button className="group rounded-2xl border border-gray-200 bg-white p-4 text-left hover:border-blue-400 hover:bg-blue-50/50 dark:border-gray-700 dark:bg-gray-800 dark:hover:border-blue-600 dark:hover:bg-blue-950/20" onClick={() => typWaehlen('umbuchung')}>
                <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-blue-100 text-blue-700 dark:bg-blue-950/40 dark:text-blue-300"><ArrowsRightLeftIcon className="h-5 w-5" /></span>
                <span className="mt-3 block font-bold text-gray-900 dark:text-white">Nur Geld zwischen eigenen Konten</span>
                <span className="mt-1 block text-sm leading-5 text-gray-600 dark:text-gray-300">Es wurde nichts gekauft; das Geld wurde nur auf ein anderes Vereinskonto verschoben.</span>
              </button>
            </div>

            <div className="rounded-2xl border border-primary-200 bg-primary-50/60 p-4 dark:border-primary-800 dark:bg-primary-900/20">
              <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                <div className="flex gap-3">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary-600 text-white"><SparklesIcon className="h-5 w-5" /></div>
                  <div>
                    <h3 className="font-bold text-gray-900 dark:text-white">Unsicher? Lass dir Buchung und Beleg erklären</h3>
                    <p className="mt-1 text-sm leading-5 text-gray-600 dark:text-gray-300">Die KI liest den verknüpften Beleg mit, nennt Indizien und stellt Rückfragen. Sie speichert keine Entscheidung.</p>
                  </div>
                </div>
                <button className="btn primary shrink-0" onClick={kiLaden} disabled={kiLaedt || kandidat.belege === 0} aria-busy={kiLaedt}>
                  <SparklesIcon className="h-4 w-4" /> {kiLaedt ? 'Beleg wird analysiert …' : kandidat.belege ? 'Mit KI erklären' : 'Kein Beleg vorhanden'}
                </button>
              </div>

              {kiFehler && <p className="mt-4 rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-950/30 dark:text-red-200">{kiFehler}</p>}
              {ki && (
                <div className="mt-5 rounded-2xl border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800">
                  <div className="flex flex-wrap items-center gap-2">
                    <EmpfehlungBadge empfehlung={ki.empfehlung} />
                    <span className="text-xs font-semibold text-gray-500 dark:text-gray-400">{confidenceLabel(ki.sicherheit)}</span>
                    <span className="text-xs text-gray-400">•</span>
                    <span className="text-xs text-gray-500 dark:text-gray-400">{ki.quelle === 'ki' ? `${ki.belege_analysiert || 0} Beleg(e) mit KI gelesen` : 'Regelbasierte Hilfe'}</span>
                  </div>
                  <h4 className="mt-3 font-bold text-gray-900 dark:text-white">{ki.kurzfassung}</h4>
                  <p className="mt-2 text-sm leading-6 text-gray-600 dark:text-gray-300">{ki.erklaerung}</p>
                  <div className="mt-4 grid gap-4 md:grid-cols-2">
                    <div>
                      <p className="text-xs font-bold uppercase tracking-wider text-gray-500">Dafür spricht</p>
                      <ul className="mt-2 space-y-2 text-sm text-gray-700 dark:text-gray-200">{ki.gruende.map((grund, index) => <li key={index} className="flex gap-2"><CheckCircleIcon className="mt-0.5 h-4 w-4 shrink-0 text-green-600" />{grund}</li>)}</ul>
                    </div>
                    <div>
                      <p className="text-xs font-bold uppercase tracking-wider text-gray-500">Bitte noch prüfen</p>
                      <ul className="mt-2 space-y-2 text-sm text-gray-700 dark:text-gray-200">{kiFragen.map((frage, index) => <li key={index} className="flex gap-2"><LightBulbIcon className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />{frage}</li>)}</ul>
                    </div>
                  </div>
                  {ki.empfehlung === 'anlage' && (
                    <button className="btn ghost sm mt-4" onClick={kiUebernehmen}>Vorschlag übernehmen und weiter <ArrowRightIcon className="h-4 w-4" /></button>
                  )}
                  <p className="mt-4 text-xs text-gray-500 dark:text-gray-400">Hinweis: Die KI ist eine Lesehilfe, keine Steuerberatung. Die Entscheidung bleibt beim Vorstand.</p>
                </div>
              )}
            </div>
          </div>
        )}

        {schritt === 2 && (
          <div className="space-y-6 p-5 sm:p-6">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.16em] text-primary-600 dark:text-primary-400">Frage 2 von 3</p>
              <h2 className="mt-1 text-xl font-bold text-gray-900 dark:text-white">Wie viele eigenständig nutzbare Gegenstände sind es?</h2>
              <p className="mt-2 max-w-3xl text-sm leading-6 text-gray-600 dark:text-gray-300">Die Wertgrenze gilt pro Gegenstand. 20 einzeln nutzbare Stühle werden anders behandelt als eine fest zusammengehörende Anlage aus 20 Bauteilen.</p>
            </div>

            <div className="grid gap-5 lg:grid-cols-[1fr_320px]">
              <div className="space-y-5">
                <div className="field">
                  <label htmlFor="anlagen-menge">Anzahl der Gegenstände</label>
                  <input id="anlagen-menge" type="number" min={1} value={form.menge} onChange={(event) => setForm((alt) => ({ ...alt, menge: Math.max(1, Number(event.target.value) || 1) }))} />
                  <p>Trage nicht Rechnungspositionen ein, sondern die Gegenstände, die jeweils für sich genutzt werden können.</p>
                </div>
                {form.menge > 1 && (
                  <fieldset>
                    <legend className="text-xs font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400">Könnte jedes Stück allein genutzt werden?</legend>
                    <div className="mt-2 grid gap-2 sm:grid-cols-2">
                      <label className={`option cursor-pointer ${form.selbststaendig_nutzbar ? 'active' : ''}`}>
                        <input className="sr-only" type="radio" name="nutzbar" checked={form.selbststaendig_nutzbar} onChange={() => setForm((alt) => ({ ...alt, selbststaendig_nutzbar: true }))} />
                        <span><strong>Ja, jedes Stück einzeln</strong><small>z. B. Stühle, Tische oder Hürden</small></span>
                      </label>
                      <label className={`option cursor-pointer ${!form.selbststaendig_nutzbar ? 'active' : ''}`}>
                        <input className="sr-only" type="radio" name="nutzbar" checked={!form.selbststaendig_nutzbar} onChange={() => setForm((alt) => ({ ...alt, selbststaendig_nutzbar: false }))} />
                        <span><strong>Nein, nur zusammen</strong><small>z. B. fest verbundene Bauteile einer Anlage</small></span>
                      </label>
                    </div>
                  </fieldset>
                )}
                <div className="field max-w-xs">
                  <label htmlFor="anlagen-ust">Umsatzsteuer auf dem Beleg</label>
                  <select id="anlagen-ust" value={form.ust_satz} onChange={(event) => setForm((alt) => ({ ...alt, ust_satz: Number(event.target.value) }))}>
                    <option value={19}>19 % Umsatzsteuer</option>
                    <option value={7}>7 % Umsatzsteuer</option>
                    <option value={0}>Keine Umsatzsteuer ausgewiesen</option>
                  </select>
                  <p>Die Grenze wird mit dem Nettowert geprüft. Maßgeblich ist der Steuersatz auf der Rechnung.</p>
                </div>
              </div>

              <aside className="rounded-2xl border border-primary-200 bg-primary-50 p-5 dark:border-primary-800 dark:bg-primary-900/20" aria-live="polite">
                <p className="text-xs font-bold uppercase tracking-wider text-primary-700 dark:text-primary-300">Live-Ergebnis</p>
                <p className="mt-2 text-lg font-bold text-gray-900 dark:text-white">{ART_LABEL[eo.art]}</p>
                <p className="mt-2 text-sm leading-6 text-gray-600 dark:text-gray-300">{eo.begruendung}</p>
                <dl className="mt-4 space-y-3 border-t border-primary-200 pt-4 text-sm dark:border-primary-800">
                  <div className="flex justify-between gap-3"><dt className="text-gray-500">Rechnung</dt><dd className="font-bold text-gray-900 dark:text-white">{euro(eo.gesamt.brutto)}</dd></div>
                  <div className="flex justify-between gap-3"><dt className="text-gray-500">Prüfwert {eo.einzelnBewerten ? 'je Stück' : ''}</dt><dd className="font-bold text-gray-900 dark:text-white">{euro(eo.netto)} netto</dd></div>
                  <div className="flex justify-between gap-3"><dt className="text-gray-500">Ins Verzeichnis</dt><dd className="font-bold text-gray-900 dark:text-white">{eo.art === 'anlagevermoegen' ? euro(eo.aktivierungssumme) : 'Nein'}</dd></div>
                </dl>
              </aside>
            </div>

            <div className="flex flex-wrap justify-between gap-3 border-t border-gray-200 pt-5 dark:border-gray-700">
              <button className="btn ghost" onClick={() => setSchritt(1)}><ArrowLeftIcon className="h-4 w-4" /> Zurück</button>
              <button className="btn primary" onClick={() => setSchritt(3)}>Ergebnis prüfen <ArrowRightIcon className="h-4 w-4" /></button>
            </div>
          </div>
        )}

        {schritt === 3 && (
          <div className="space-y-6 p-5 sm:p-6">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.16em] text-primary-600 dark:text-primary-400">Frage 3 von 3</p>
              <h2 className="mt-1 text-xl font-bold text-gray-900 dark:text-white">Passt diese Entscheidung?</h2>
              <p className="mt-2 text-sm leading-6 text-gray-600 dark:text-gray-300">Prüfe die Zusammenfassung. Erst dein Klick speichert die Entscheidung.</p>
            </div>

            <section className="overflow-hidden rounded-2xl border border-primary-200 bg-primary-50/60 dark:border-primary-800 dark:bg-primary-900/20" aria-labelledby="anlagen-ki-fragen-title">
              <div className="flex gap-3 border-b border-primary-200 p-4 dark:border-primary-800 sm:p-5">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary-600 text-white">
                  <ChatBubbleLeftRightIcon className="h-5 w-5" />
                </div>
                <div>
                  <h3 id="anlagen-ki-fragen-title" className="font-bold text-gray-900 dark:text-white">Noch unsicher? Frag die KI</h3>
                  <p className="mt-1 text-sm leading-5 text-gray-600 dark:text-gray-300">
                    Die Antwort berücksichtigt diese Buchung, das Konto und deine bisherigen Angaben. Es wird nichts gespeichert oder verändert.
                  </p>
                </div>
              </div>

              <div className="space-y-4 bg-white/80 p-4 dark:bg-gray-800/70 sm:p-5">
                {kiDialog.length === 0 && (
                  <div className="flex flex-wrap gap-2" aria-label="Beispielfragen">
                    {[
                      'Warum ist das eine Anlage?',
                      'Welche Angabe sollte ich noch prüfen?',
                      'Passt die Nutzungsdauer?'
                    ].map((beispiel) => (
                      <button
                        key={beispiel}
                        type="button"
                        className="min-h-10 rounded-full border border-gray-200 bg-white px-3 py-2 text-left text-xs font-bold text-gray-700 transition-colors hover:border-primary-300 hover:text-primary-700 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200 dark:hover:border-primary-600 dark:hover:text-primary-300"
                        onClick={() => setKiFrage(beispiel)}
                      >
                        {beispiel}
                      </button>
                    ))}
                  </div>
                )}

                {kiDialog.length > 0 && (
                  <div className="max-h-80 space-y-3 overflow-y-auto pr-1" aria-live="polite">
                    {kiDialog.map((nachricht, index) => (
                      <div key={`${nachricht.rolle}-${index}`} className={`flex ${nachricht.rolle === 'user' ? 'justify-end' : 'justify-start'}`}>
                        <div className={`max-w-[90%] rounded-2xl border px-4 py-3 text-sm leading-6 sm:max-w-[80%] ${
                          nachricht.rolle === 'user'
                            ? 'border-primary-600 bg-primary-600 text-white'
                            : 'border-gray-200 bg-white text-gray-700 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200'
                        }`}>
                          <p className="whitespace-pre-wrap">{nachricht.text}</p>
                          {nachricht.rolle === 'assistant' && nachricht.quelle && (
                            <p className="mt-2 text-[11px] font-bold uppercase tracking-wider text-gray-400">
                              {nachricht.quelle === 'ki' ? 'KI-Antwort' : 'Antwort aus dem Regelwerk'}
                            </p>
                          )}
                        </div>
                      </div>
                    ))}
                    {kiAntwortLaedt && (
                      <div className="flex justify-start">
                        <div className="flex items-center gap-2 rounded-2xl border border-gray-200 bg-white px-4 py-3 text-sm text-gray-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-300">
                          <span className="spinner" aria-hidden="true" /> KI prüft den Kontext …
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {kiDialogFehler && (
                  <p className="rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-950/30 dark:text-red-200" role="alert">
                    {kiDialogFehler}
                  </p>
                )}

                <form className="flex flex-col gap-2 sm:flex-row sm:items-end" onSubmit={kiFrageSenden}>
                  <div className="field min-w-0 flex-1">
                    <label htmlFor="anlagen-ki-frage">Deine Frage</label>
                    <textarea
                      id="anlagen-ki-frage"
                      rows={2}
                      maxLength={1000}
                      value={kiFrage}
                      onChange={(event) => setKiFrage(event.target.value)}
                      placeholder="z. B. Warum wird der Brunnenbau als Anlage eingeordnet?"
                    />
                  </div>
                  <button className="btn primary min-h-11 shrink-0" type="submit" disabled={!kiFrage.trim() || kiAntwortLaedt} aria-busy={kiAntwortLaedt}>
                    <PaperAirplaneIcon className="h-4 w-4" /> {kiAntwortLaedt ? 'Wird geprüft …' : 'Frage senden'}
                  </button>
                </form>
                <p className="text-xs text-gray-500 dark:text-gray-400">Keine Steuerberatung: Die endgültige Entscheidung bleibt beim Vorstand.</p>
              </div>
            </section>

            {typ === 'gegenstand' ? (
              <div className="space-y-5">
                <div className={`rounded-2xl border p-4 ${eo.art === 'anlagevermoegen' ? 'border-primary-200 bg-primary-50 dark:border-primary-800 dark:bg-primary-900/20' : 'border-green-200 bg-green-50 dark:border-green-800 dark:bg-green-950/20'}`}>
                  <p className="text-sm font-bold text-gray-900 dark:text-white">Ergebnis: {ART_LABEL[eo.art]}</p>
                  <p className="mt-1 text-sm leading-6 text-gray-600 dark:text-gray-300">{eo.begruendung}</p>
                </div>

                {eo.art === 'anlagevermoegen' && (
                  <div className="grid gap-5 md:grid-cols-2">
                    <div className="field md:col-span-2">
                      <label htmlFor="anlagen-bezeichnung">Verständliche Bezeichnung</label>
                      <input id="anlagen-bezeichnung" value={form.bezeichnung} onChange={(event) => setForm((alt) => ({ ...alt, bezeichnung: event.target.value }))} placeholder="z. B. Aufsitzmäher Trainingsplatz" />
                      <p>Keine IBAN, Rechnungsnummer oder Banktexte – nenne den Gegenstand so, wie ihr ihn im Verein nennt.</p>
                    </div>
                    <div className="field">
                      <label htmlFor="anlagen-kategorie">Art des Gegenstands</label>
                      <select id="anlagen-kategorie" value={form.kategorie} onChange={(event) => {
                        const item = katalog.find((wert) => wert.key === event.target.value);
                        setForm((alt) => ({ ...alt, kategorie: event.target.value, nutzungsdauer: item?.jahre || alt.nutzungsdauer }));
                      }}>
                        {katalog.map((item) => <option key={item.key} value={item.key}>{item.label}</option>)}
                      </select>
                      <p>{aktiveKategorie?.beispiele || 'Wähle die passendste Kategorie.'}</p>
                    </div>
                    <div className="field">
                      <label htmlFor="anlagen-nutzungsdauer">Nutzungsdauer in Jahren</label>
                      <input id="anlagen-nutzungsdauer" type="number" min={1} value={form.nutzungsdauer} onChange={(event) => setForm((alt) => ({ ...alt, nutzungsdauer: Math.max(1, Number(event.target.value) || 1) }))} />
                      <p>{aktiveKategorie?.geprueft ? aktiveKategorie.quelle : `Vorschlagswert – ${aktiveKategorie?.quelle || 'bitte fachlich prüfen'}`}</p>
                    </div>
                    <div className="field">
                      <label htmlFor="anlagen-sphaere">Wo wird der Gegenstand eingesetzt?</label>
                      <select id="anlagen-sphaere" value={form.sphaere} onChange={(event) => setForm((alt) => ({ ...alt, sphaere: event.target.value }))}>
                        {SPHAEREN.map((item) => <option key={item.key} value={item.key}>{item.label} – {item.hilfe}</option>)}
                      </select>
                    </div>
                    <div className="field">
                      <label htmlFor="anlagen-inbetriebnahme">Seit wann wird er genutzt?</label>
                      <input id="anlagen-inbetriebnahme" type="date" value={form.inbetriebnahme} onChange={(event) => setForm((alt) => ({ ...alt, inbetriebnahme: event.target.value }))} />
                      <p>Die Abschreibung beginnt mit dem Monat der tatsächlichen Nutzung.</p>
                    </div>
                    <div className="field md:col-span-2">
                      <label htmlFor="anlagen-standort">Standort (optional)</label>
                      <input id="anlagen-standort" value={form.standort} onChange={(event) => setForm((alt) => ({ ...alt, standort: event.target.value }))} placeholder="z. B. Geräteschuppen oder Vereinsheim" />
                    </div>
                  </div>
                )}

                <div className="flex flex-wrap justify-between gap-3 border-t border-gray-200 pt-5 dark:border-gray-700">
                  <button className="btn ghost" onClick={() => setSchritt(2)}><ArrowLeftIcon className="h-4 w-4" /> Zurück</button>
                  {eo.art === 'anlagevermoegen' ? (
                    <button className="btn primary" disabled={speichert || !form.bezeichnung.trim()} aria-busy={speichert} onClick={() => onEntscheiden('anlage')}>
                      <CheckCircleIcon className="h-4 w-4" /> {speichert ? 'Wird gespeichert …' : 'Ins Anlagenverzeichnis übernehmen'}
                    </button>
                  ) : (
                    <button className="btn primary" disabled={speichert} aria-busy={speichert} onClick={() => onEntscheiden('kein_anlagegut', `${ART_LABEL[eo.art]}: ${eo.begruendung}`)}>
                      <CheckCircleIcon className="h-4 w-4" /> {speichert ? 'Wird gespeichert …' : `${ART_LABEL[eo.art]} bestätigen`}
                    </button>
                  )}
                </div>
              </div>
            ) : (
              <div className="space-y-5">
                <div className="rounded-2xl border border-gray-200 bg-gray-50 p-5 dark:border-gray-700 dark:bg-gray-800/70">
                  <div className="flex gap-3">
                    {typ === 'umbuchung' ? <ArrowsRightLeftIcon className="h-6 w-6 shrink-0 text-blue-600" /> : <XCircleIcon className="h-6 w-6 shrink-0 text-amber-600" />}
                    <div>
                      <h3 className="font-bold text-gray-900 dark:text-white">{typ === 'umbuchung' ? 'Als Umbuchung markieren' : 'Nicht ins Anlagenverzeichnis übernehmen'}</h3>
                      <p className="mt-1 text-sm leading-6 text-gray-600 dark:text-gray-300">{typ === 'umbuchung' ? 'Die Zahlung wird im Prüfdurchlauf als Geldbewegung zwischen eigenen Konten dokumentiert.' : 'Die Zahlung bleibt Aufwand in der Finanzbuchhaltung. Es wird kein Gegenstand und keine Abschreibung angelegt.'}</p>
                    </div>
                  </div>
                </div>
                <div className="flex flex-wrap justify-between gap-3 border-t border-gray-200 pt-5 dark:border-gray-700">
                  <button className="btn ghost" onClick={() => setSchritt(1)}><ArrowLeftIcon className="h-4 w-4" /> Auswahl ändern</button>
                  <button className="btn primary" disabled={speichert} aria-busy={speichert} onClick={() => onEntscheiden(typ === 'umbuchung' ? 'umbuchung' : 'kein_anlagegut', typ === 'umbuchung' ? 'Geldbewegung zwischen eigenen Konten' : 'Laufende Leistung oder Aufwand des Jahres')}>
                    <CheckCircleIcon className="h-4 w-4" /> {speichert ? 'Wird gespeichert …' : 'Entscheidung bestätigen'}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </section>

      <div className="flex flex-wrap items-center justify-between gap-3 px-1">
        <button className="inline-flex min-h-10 items-center gap-2 text-sm font-semibold text-gray-500 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200" disabled={speichert} onClick={() => onEntscheiden('zurueckgestellt', 'Im Durchlauf zurückgestellt')}>
          <ClockIcon className="h-4 w-4" /> Diese Buchung später prüfen
        </button>
        <p className="text-xs text-gray-500 dark:text-gray-400">Es wird nichts automatisch gebucht.</p>
      </div>
    </div>
  );
};

export default AnlagenAssistant;
